/**
 * Shared motion system.
 *
 * One source of truth for animation so the whole app feels consistent and
 * stays on-charter:
 *  - Durations are DERIVED from theme.motion (the design tokens) so they can
 *    never silently drift from the system.
 *  - Every variant factory takes a `reduce` flag and collapses to an instant,
 *    transform-free state when it's true — so a consumer literally cannot ship
 *    an animation that ignores `prefers-reduced-motion`.
 *
 * Usage:
 *   const { reduce, d } = useMotion();
 *   <motion.div variants={fadeUp(reduce)} initial="hidden" animate="show" />
 *   <motion.button {...pressable(reduce)} />
 *   transition={{ duration: d("base") }}
 */
import { Transition, Variants, useReducedMotion } from "framer-motion";
import { theme } from "./theme";

// "150ms" -> 0.15 (framer-motion works in seconds). Parsed from the tokens so
// DUR and theme.motion never diverge.
const toSeconds = (token: string) => parseFloat(token) / 1000;

export const DUR = {
  fast: toSeconds(theme.motion.fast),
  base: toSeconds(theme.motion.base),
  slow: toSeconds(theme.motion.slow),
} as const;

export type DurToken = keyof typeof DUR;

const OUT = "easeOut" as const;

export const SPRING: Transition = { type: "spring", stiffness: 300, damping: 30 };
export const SPRING_SOFT: Transition = { type: "spring", stiffness: 220, damping: 26 };

/* ─── Entrance variants ─── */
export const fadeUp = (reduce: boolean, dur: DurToken = "base"): Variants => ({
  hidden: { opacity: 0, y: reduce ? 0 : 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: reduce ? 0 : DUR[dur], ease: OUT },
  },
});

export const fadeIn = (reduce: boolean, dur: DurToken = "base"): Variants => ({
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: reduce ? 0 : DUR[dur], ease: OUT } },
});

export const scaleIn = (reduce: boolean, dur: DurToken = "base"): Variants => ({
  hidden: { opacity: 0, scale: reduce ? 1 : 0.95 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: reduce ? 0 : DUR[dur], ease: OUT },
  },
});

export const slideIn = (
  reduce: boolean,
  dir: "left" | "right" | "up" | "down" = "right",
  dur: DurToken = "base"
): Variants => {
  const axis = dir === "left" || dir === "right" ? "x" : "y";
  const sign = dir === "right" || dir === "down" ? 1 : -1;
  const offset = reduce ? 0 : 20 * sign;
  return {
    hidden: { opacity: 0, [axis]: offset },
    show: {
      opacity: 1,
      [axis]: 0,
      transition: { duration: reduce ? 0 : DUR[dur], ease: OUT },
    },
  };
};

/** Parent that staggers its children's entrance. Pair with fadeUp items. */
export const staggerContainer = (reduce: boolean, gap = 0.05): Variants => ({
  hidden: {},
  show: { transition: { staggerChildren: reduce ? 0 : gap } },
});

/** AnimatePresence list row (e.g. cart item) — enters from the right, leaves left. */
export const listItem = (reduce: boolean): Variants => ({
  hidden: { opacity: 0, x: reduce ? 0 : 24, height: reduce ? "auto" : undefined },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: reduce ? 0 : DUR.base, ease: OUT },
  },
  exit: {
    opacity: 0,
    x: reduce ? 0 : -24,
    transition: { duration: reduce ? 0 : DUR.fast, ease: OUT },
  },
});

/* ─── Overlay variants (dialog / sheet / drawer / dropdown panel) ─── */
export const overlayPanel = (reduce: boolean): Variants =>
  reduce
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0 } },
        exit: { opacity: 0, transition: { duration: 0 } },
      }
    : {
        hidden: { opacity: 0, scale: 0.96, y: 8 },
        show: { opacity: 1, scale: 1, y: 0, transition: SPRING_SOFT },
        exit: { opacity: 0, scale: 0.97, y: 6, transition: { duration: DUR.fast, ease: OUT } },
      };

/** Dropdown menu that grows from its top edge. */
export const dropdownPanel = (reduce: boolean): Variants =>
  reduce
    ? {
        hidden: { opacity: 0 },
        show: { opacity: 1, transition: { duration: 0 } },
        exit: { opacity: 0, transition: { duration: 0 } },
      }
    : {
        hidden: { opacity: 0, scaleY: 0.9, y: -6 },
        show: { opacity: 1, scaleY: 1, y: 0, transition: { duration: DUR.base, ease: OUT } },
        exit: { opacity: 0, scaleY: 0.95, y: -4, transition: { duration: DUR.fast, ease: OUT } },
      };

export const backdrop = (reduce: boolean): Variants => ({
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: reduce ? 0 : DUR.fast } },
  exit: { opacity: 0, transition: { duration: reduce ? 0 : DUR.fast } },
});

/* ─── Interaction props (spread onto a motion component) ─── */
export const tapProps = (reduce: boolean) => (reduce ? {} : { whileTap: { scale: 0.96 } });

export const pressable = (reduce: boolean) =>
  reduce ? {} : { whileHover: { y: -2 }, whileTap: { scale: 0.97 } };

/* ─── Hook ─── */
export const useMotion = () => {
  const reduce = !!useReducedMotion();
  return {
    reduce,
    /** Duration in seconds for a token, or 0 under reduced motion. */
    d: (token: DurToken = "base") => (reduce ? 0 : DUR[token]),
    DUR,
    SPRING,
    SPRING_SOFT,
  };
};
